import { useEffect, useRef } from 'react';

export const IS_TOUCH_DEVICE = typeof window !== 'undefined' && ('ontouchstart' in window || (navigator.maxTouchPoints || 0) > 0);
const nativeCallBridge = () => (window.AndroidBridge && typeof window.AndroidBridge.callStarted === 'function' ? window.AndroidBridge : null);
export const HAS_NATIVE_CALL_KEEPALIVE = !!nativeCallBridge();
export const LOCK_HINT = HAS_NATIVE_CALL_KEEPALIVE
  ? 'You can lock your phone — the call keeps running in the background.'
  : 'Keep your phone unlocked during the call — locking the screen or switching apps mutes your microphone.';

/**
 * Keeps the screen awake while a call is active (so Android/iOS never cut the
 * microphone by locking) and calls `onResume` whenever the page comes back to
 * the foreground so the caller can re-acquire tracks the OS muted meanwhile.
 * Inside the BBC Android app it also starts the native microphone/camera
 * foreground service, which lets the call survive a locked screen.
 */
export function useCallKeepAlive(active, onResume, opts) {
  const lockRef = useRef(null);
  const resumeRef = useRef(onResume);
  resumeRef.current = onResume;
  const title = (opts && opts.title) || '';
  const video = !!(opts && opts.video);

  useEffect(() => {
    if (!active) return undefined;
    const bridge = nativeCallBridge();
    if (bridge) { try { bridge.callStarted(title, video); } catch (e) { /* older app */ } }
    return () => { if (bridge) { try { bridge.callEnded(); } catch (e) { /* noop */ } } };
  }, [active, title, video]);

  useEffect(() => {
    if (!active) return undefined;
    let disposed = false;

    const acquire = async () => {
      if (disposed || document.visibilityState !== 'visible' || !navigator.wakeLock) return;
      if (lockRef.current && !lockRef.current.released) return;
      try {
        lockRef.current = await navigator.wakeLock.request('screen');
      } catch (e) { /* low battery / unsupported — ignore */ }
    };

    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      acquire();
      if (resumeRef.current) resumeRef.current();
    };

    acquire();
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('pageshow', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      disposed = true;
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('pageshow', onVisible);
      window.removeEventListener('focus', onVisible);
      const l = lockRef.current; lockRef.current = null;
      if (l) l.release().catch(() => {});
    };
  }, [active]);
}
